import { apiFetch } from "../auth/apiClient";

// Governance data changes under the user (support, votes, RSVPs, new
// motions), so never let the browser serve a stale cached list/detail — the
// counts and "needs you" surfaces must always reflect the live state.
const g = (path, opts = {}) =>
  apiFetch(`/api/governance${path}`, {
    authRequired: true,
    cache: "no-store",
    ...opts,
  });

export const governanceApi = {
  overview: () => g("/overview"),
  // Everything waiting on the current member — support / vote / poll / RSVP.
  myAgenda: () => g("/my-agenda"),

  // ===== Issues =====
  listIssues: ({ kind, status, mine, order, limit, offset } = {}) => {
    const qs = new URLSearchParams();
    if (kind) qs.set("kind", kind);
    if (status) qs.set("status", status);
    if (mine) qs.set("mine", "true");
    if (order) qs.set("order", order);
    if (limit != null) qs.set("limit", limit);
    if (offset != null) qs.set("offset", offset);
    const s = qs.toString();
    return g(`/issues${s ? `?${s}` : ""}`);
  },
  createIssue: (body) => g("/issues", { method: "POST", body }),
  getIssue: (id) => g(`/issues/${id}`),
  supportIssue: (id) => g(`/issues/${id}/support`, { method: "POST" }),
  withdrawSupport: (id) => g(`/issues/${id}/support`, { method: "DELETE" }),
  answerIssue: (id, answer) =>
    g(`/issues/${id}/answer`, { method: "POST", body: { answer } }),
  closeIssue: (id, reason) =>
    g(`/issues/${id}/close`, { method: "POST", body: { reason } }),

  // ===== Meetings =====
  listMeetings: ({ status, kind, order, limit, offset } = {}) => {
    const qs = new URLSearchParams();
    if (status) qs.set("status", status);
    if (kind) qs.set("kind", kind);
    if (order) qs.set("order", order);
    if (limit != null) qs.set("limit", limit);
    if (offset != null) qs.set("offset", offset);
    const s = qs.toString();
    return g(`/meetings${s ? `?${s}` : ""}`);
  },
  createMeeting: (body) => g("/meetings", { method: "POST", body }),
  getMeeting: (id) => g(`/meetings/${id}`),
  patchMeeting: (id, body) => g(`/meetings/${id}`, { method: "PATCH", body }),
  addAgendaItem: (id, body) =>
    g(`/meetings/${id}/agenda`, { method: "POST", body }),
  removeAgendaItem: (id, itemId) =>
    g(`/meetings/${id}/agenda/${itemId}`, { method: "DELETE" }),
  issueNotice: (id) => g(`/meetings/${id}/issue-notice`, { method: "POST" }),
  rsvp: (id, response) =>
    g(`/meetings/${id}/rsvp`, {
      method: "POST",
      body: { response, channel: "WEB" },
    }),
  rescheduleMeeting: (id, body) =>
    g(`/meetings/${id}/reschedule`, { method: "POST", body }),
  markHeld: (id) => g(`/meetings/${id}/mark-held`, { method: "POST" }),
  cancelMeeting: (id, reason) =>
    g(`/meetings/${id}/cancel`, { method: "POST", body: { reason } }),

  // ===== Resolutions =====
  proposeResolution: (meetingId, body) =>
    g(`/meetings/${meetingId}/resolutions`, { method: "POST", body }),
  recordFloorResolution: (meetingId, body) =>
    g(`/meetings/${meetingId}/floor-resolutions`, { method: "POST", body }),
  voteResolution: (rid, vote) =>
    g(`/resolutions/${rid}/vote`, { method: "POST", body: { vote } }),
  decideResolution: (rid) => g(`/resolutions/${rid}/decide`, { method: "POST" }),
  // Bare call (no status) returns the decided register (passed + did-not-pass).
  listResolutions: ({ status, order, limit, offset } = {}) => {
    const qs = new URLSearchParams();
    if (status) qs.set("status", status);
    if (order) qs.set("order", order);
    if (limit != null) qs.set("limit", limit);
    if (offset != null) qs.set("offset", offset);
    const s = qs.toString();
    return g(`/resolutions${s ? `?${s}` : ""}`);
  },

  // ===== Polls (advisory "sense of the house") =====
  createPoll: (meetingId, body) =>
    g(`/meetings/${meetingId}/polls`, { method: "POST", body }),
  votePoll: (pollId, optionId) =>
    g(`/polls/${pollId}/vote`, { method: "POST", body: { option_id: optionId } }),
  closePoll: (pollId) => g(`/polls/${pollId}/close`, { method: "POST" }),

  // ===== Audit trail =====
  trail: (targetId) =>
    g(`/trail?target_id=${encodeURIComponent(targetId)}`),
};

// ---------------------------------------------------------------------------
// Display metadata
// ---------------------------------------------------------------------------

export const ISSUE_KIND_META = {
  TOPIC: { label: "Topic", pill: "open" },
  QUERY: { label: "Query", pill: "p2" },
  DISPUTE: { label: "Dispute", pill: "warning" },
  MEETING_REQUEST: { label: "Meeting request", pill: "p1" },
};

export const ISSUE_STATUS_META = {
  OPEN: { label: "Open", pill: "open" },
  THRESHOLD_MET: { label: "Threshold met", pill: "warning" },
  SCHEDULED: { label: "Scheduled", pill: "in-progress" },
  IN_MEDIATION: { label: "In mediation", pill: "in-progress" },
  ANSWERED: { label: "Answered", pill: "success" },
  RESOLVED: { label: "Resolved", pill: "done" },
  CLOSED: { label: "Closed", pill: "neutral" },
};

export const MEETING_KIND_META = {
  GBM: { label: "GBM", pill: "p1" },
  OWNERS: { label: "Owners' meeting", pill: "open" },
  MEDIATION: { label: "Mediation", pill: "p2" },
};

export const MEETING_STATUS_META = {
  DRAFT: { label: "Draft", pill: "neutral" },
  NOTICE_ISSUED: { label: "Notice issued", pill: "open" },
  RESCHEDULED: { label: "Rescheduled", pill: "warning" },
  HELD: { label: "Held", pill: "done" },
  CANCELLED: { label: "Cancelled", pill: "danger" },
};

export const RESOLUTION_STATUS_META = {
  PROPOSED: { label: "Proposed", pill: "open" },
  PASSED: { label: "Passed", pill: "done" },
  REJECTED: { label: "Rejected", pill: "danger" },
  WITHDRAWN: { label: "Withdrawn", pill: "neutral" },
};

export const RESOLUTION_ORIGIN_META = {
  NOTICE: { label: "Circulated with notice", pill: "neutral" },
  FLOOR: { label: "Tabled in meeting", pill: "warning" },
};

export const RSVP_CHANNEL_LABELS = {
  WHATSAPP: "WhatsApp",
  APP: "App",
  WEB: "Web",
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** "Sun 5 Jul 2026, 10:00 AM" */
export function formatDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const date = d.toLocaleDateString("en-IN", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
  const time = d.toLocaleTimeString("en-IN", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
  return `${date}, ${time.toUpperCase()}`;
}

/** "Sun 5 Jul 2026" */
export function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-IN", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/** Whole days from now until `iso` (negative = past). */
export function daysUntil(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return Math.ceil((d.getTime() - Date.now()) / 86400000);
}

/** datetime-local input value → ISO string (or null). */
export function localInputToIso(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

/** ISO string → datetime-local input value in local time. */
export function isoToLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
}

/** Friendly message from an apiFetch error. */
export function errMessage(e) {
  if (e && typeof e.detail === "string") return e.detail;
  if (e && e.message) return e.message;
  return "Something went wrong";
}

const EVENT_LABELS = {
  "governance.issue.raised": "Issue raised",
  "governance.issue.supported": "Support added",
  "governance.issue.support_withdrawn": "Support withdrawn",
  "governance.issue.answered": "Board answered",
  "governance.issue.closed": "Issue closed",
  "governance.issue.threshold_met": "Support threshold met",
  "governance.issue.no_confidence_raised": "No-confidence motion raised",
  "governance.meeting.created": "Meeting created",
  "governance.meeting.updated": "Meeting updated",
  "governance.agenda.added": "Agenda item added",
  "governance.agenda.removed": "Agenda item removed",
  "governance.meeting.notice_issued": "Notice issued",
  "governance.meeting.rescheduled": "Meeting rescheduled",
  "governance.meeting.held": "Meeting held",
  "governance.meeting.cancelled": "Meeting cancelled",
  "governance.rsvp.recorded": "RSVP recorded",
  "governance.resolution.proposed": "Resolution proposed",
  "governance.resolution.recorded": "In-meeting resolution recorded",
  "governance.resolution.voted": "Vote cast",
  "governance.resolution.decided": "Resolution decided",
  "governance.whatsapp.fanout": "WhatsApp messages sent",
  "governance.app.locked": "Platform locked",
  "governance.app.unlocked": "Platform unlocked",
};

export function humanizeEvent(eventType) {
  if (!eventType) return "Event";
  if (EVENT_LABELS[eventType]) return EVENT_LABELS[eventType];
  const tail = eventType.replace(/^governance\./, "");
  const text = tail.replace(/[._]/g, " ").trim();
  return text.charAt(0).toUpperCase() + text.slice(1);
}
